<script setup lang="ts">
import { computed, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref, watch, watchEffect } from 'vue';
import { useScanner, DEBUG } from '../composables/useScanner';
import { frameRectToDisplay } from '../../engine/camera/viewTransform';
import type { Rect } from '../../engine/types';
import { sanitizeRegistration, validateRegistration } from '../api/checkVehicle';
import PrimingScreen from '../components/PrimingScreen.vue';
import ResultCard from '../components/ResultCard.vue';
import HistoryPanel from '../components/HistoryPanel.vue';
import DebugOverlay from '../components/DebugOverlay.vue';
import ZoomSlider from '../components/ZoomSlider.vue';
import ManualLookupForm from '../components/ManualLookupForm.vue';
import { scanImageFile } from '../composables/imageScanner';
import { listScans } from '../../engine/history/db';

const videoRef = ref<HTMLVideoElement | null>(null);
const stageRef = ref<HTMLDivElement | null>(null);
const scanner = useScanner(videoRef);
const {
  phase,
  mode,
  errorMsg,
  modelProgress,
  camState,
  camera,
  vote,
  locked,
  overlay,
  debug,
  suggestGuide,
  lockFlash,
  guideRectDisplay,
  elemSize,
} = scanner;

const showHistory = ref(false);
const historyFilter = ref<'all' | 'fav'>('all');
const hasHistory = ref(false);
const hasFavorites = ref(false);
const lookupStatus = ref<'idle' | 'loading' | 'error'>('idle');

async function refreshHistoryPresence(): Promise<void> {
  const records = await listScans();
  hasHistory.value = records.length > 0;
  hasFavorites.value = records.some((r) => r.favorite);
}

function openHistory(filter: 'all' | 'fav'): void {
  historyFilter.value = filter;
  showHistory.value = true;
}
const lookupError = ref<string | null>(null);
const scannerDisabled = new URLSearchParams(location.search).get('scanner') === '0';

// Gallery parsing: run the OCR pipeline on a chosen photo instead of the camera.
const galleryInput = ref<HTMLInputElement | null>(null);
const galleryScanning = ref(false);
const galleryError = ref(false);

function pickGallery(): void {
  galleryInput.value?.click();
}

async function onGalleryFile(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = ''; // allow re-picking the same file
  if (!file) return;
  galleryScanning.value = true;
  galleryError.value = false;
  try {
    const result = await scanImageFile(file);
    if (!result) {
      galleryError.value = true;
      setTimeout(() => (galleryError.value = false), 2600);
      return;
    }
    lookupPlate(result.text, result.region);
  } catch {
    galleryError.value = true;
    setTimeout(() => (galleryError.value = false), 2600);
  } finally {
    galleryScanning.value = false;
  }
}

const emit = defineEmits<{ lookup: [payload: { plate: string; region?: string }] }>();

let ro: ResizeObserver | null = null;
onMounted(() => {
  void refreshHistoryPresence();
  if (scannerDisabled) phase.value = 'priming';
  else void scanner.begin();
  ro = new ResizeObserver(() => {
    const el = stageRef.value;
    if (el) {
      elemSize.w = el.clientWidth;
      elemSize.h = el.clientHeight;
    }
  });
  if (stageRef.value) ro.observe(stageRef.value);
});
onBeforeUnmount(() => {
  ro?.disconnect();
  scanner.destroy();
});

// KeepAlive (App.vue) preserves this view while the details screen is on top,
// so the model worker never reloads. Release the camera meanwhile; re-acquire
// and resume scanning on return.
onDeactivated(() => scanner.pause());
onActivated(() => {
  void scanner.resume();
  void refreshHistoryPresence();
});

// Guide box: centered, 78% width, plate-ish aspect, slightly above center.
watchEffect(() => {
  if (mode.value !== 'guide' || elemSize.w === 0) {
    guideRectDisplay.value = null;
    return;
  }
  const w = elemSize.w * 0.78;
  const h = w / 4.2;
  guideRectDisplay.value = {
    x: (elemSize.w - w) / 2,
    y: elemSize.h * 0.42 - h / 2,
    w,
    h,
  };
});

// Reticle: worker reports boxes in processed-frame coords; the visible preview is
// exactly that processed crop, so map with zoom=1 (see viewTransform notes).
const reticleStyle = computed(() => {
  if (!overlay.box || overlay.frameW === 0 || elemSize.w === 0) return null;
  const r: Rect = frameRectToDisplay(overlay.box, {
    frameW: overlay.frameW,
    frameH: overlay.frameH,
    elemW: elemSize.w,
    elemH: elemSize.h,
    zoom: 1,
    mirrored: camState.value?.mirrored ?? false,
  });
  return {
    left: `${r.x}px`,
    top: `${r.y}px`,
    width: `${r.w}px`,
    height: `${r.h}px`,
  };
});

const previewScale = computed(() => camState.value?.zoom.digital ?? 1);

const ghost = computed(() => {
  const v = vote.value;
  if (!v || v.status === 'idle' || phase.value !== 'scanning') return null;
  return v.text.split('').map((ch, i) => ({ ch, share: v.charShares[i] ?? 0 }));
});

async function copyText(text: string): Promise<void> {
  await navigator.clipboard.writeText(text).catch(() => undefined);
}

// Details opens immediately; all vehicle data loads non-blocking over there.
// Only plate validation can stop navigation here.
function lookupPlate(rawPlate: string, region?: string): void {
  const validationError = validateRegistration(rawPlate);
  if (validationError) {
    lookupStatus.value = 'error';
    lookupError.value = validationError;
    return;
  }
  lookupStatus.value = 'idle';
  lookupError.value = null;
  emit('lookup', {
    plate: sanitizeRegistration(rawPlate),
    ...(region ? { region } : {}),
  });
}

function reload(): void {
  location.reload();
}

function switchToGuide(): void {
  scanner.setMode('guide');
}

watch(mode, () => {
  // keep pump params coherent when the user toggles modes mid-scan
  overlay.box = null;
});

watch(
  () => locked.value?.text,
  (text) => {
    if (text) lookupPlate(text, locked.value?.region);
  },
);

async function rescan(): Promise<void> {
  lookupStatus.value = 'idle';
  lookupError.value = null;
  await scanner.rescan();
}
</script>

<template>
  <div class="scan-root">
    <!-- camera stage -->
    <div ref="stageRef" class="stage" :class="{ flash: lockFlash }">
      <video
        ref="videoRef"
        class="preview"
        :style="{ transform: `scale(${previewScale})` }"
        playsinline
        muted
        autoplay
      ></video>

      <!-- tracking reticle (auto mode) -->
      <div
        v-if="mode === 'auto' && reticleStyle && phase === 'scanning'"
        class="reticle"
        :style="reticleStyle"
      ></div>

      <!-- guide box (guide mode) -->
      <div
        v-if="mode === 'guide' && guideRectDisplay && phase === 'scanning'"
        class="guide-box"
        :style="{
          left: `${guideRectDisplay.x}px`,
          top: `${guideRectDisplay.y}px`,
          width: `${guideRectDisplay.w}px`,
          height: `${guideRectDisplay.h}px`,
        }"
      >
        <span class="guide-hint">{{ $t('scan.alignPlate') }}</span>
      </div>

      <!-- candidate ghost -->
      <div v-if="ghost" class="ghost">
        <span
          v-for="(g, i) in ghost"
          :key="i"
          class="ghost-char"
          :style="{ opacity: 0.25 + 0.75 * g.share }"
          >{{ g.ch }}</span
        >
      </div>
    </div>

    <!-- boot: model loading. History/favorites stay reachable here — waiting
         for the scanner models must not lock the user out of past scans. -->
    <div v-if="phase === 'boot'" class="sheet center">
      <img class="brand-logo" src="/brand/autospy-logo.png" alt="AutoSpy" />
      <p class="muted">{{ $t('boot.loading') }}</p>
      <div class="progress"><div class="bar" :style="{ width: `${modelProgress * 100}%` }" /></div>
      <ManualLookupForm
        :busy="lookupStatus === 'loading'"
        :error="lookupError"
        @lookup="lookupPlate"
      />
      <div v-if="hasHistory || hasFavorites" class="priming-actions boot-actions">
        <button
          v-if="hasHistory"
          class="btn square"
          :title="$t('history.title')"
          :aria-label="$t('history.title')"
          @click="openHistory('all')"
        >
          ≡
        </button>
        <button
          v-if="hasFavorites"
          class="btn square"
          :title="$t('history.favorites')"
          :aria-label="$t('history.favorites')"
          @click="openHistory('fav')"
        >
          ★
        </button>
      </div>
    </div>

    <PrimingScreen
      v-else-if="phase === 'priming' || phase === 'denied'"
      :denied="phase === 'denied'"
      :lookup-busy="lookupStatus === 'loading'"
      :lookup-error="lookupError"
      :has-history="hasHistory"
      :has-favorites="hasFavorites"
      @enable="scanner.requestCamera()"
      @lookup="lookupPlate"
      @history="openHistory('all')"
      @favorites="openHistory('fav')"
    />

    <div v-else-if="phase === 'error'" class="sheet center">
      <h2>{{ $t('error.title') }}</h2>
      <p class="muted">{{ errorMsg }}</p>
      <button class="btn" @click="reload">{{ $t('error.reload') }}</button>
    </div>

    <!-- scanning controls -->
    <template v-if="phase === 'scanning' || phase === 'locked'">
      <div class="top-bar">
        <div class="mode-toggle" role="tablist">
          <button
            class="seg"
            :class="{ active: mode === 'auto' }"
            @click="scanner.setMode('auto')"
          >
            {{ $t('scan.auto') }}
          </button>
          <button
            class="seg"
            :class="{ active: mode === 'guide' }"
            @click="scanner.setMode('guide')"
          >
            {{ $t('scan.guide') }}
          </button>
        </div>
        <div class="top-actions">
          <button
            v-if="camState?.torchSupported"
            class="icon-btn"
            :class="{ on: camState?.torchOn }"
            :aria-label="$t('aria.torch')"
            @click="camera?.toggleTorch()"
          >
            ⚡︎
          </button>
          <button
            v-if="(camState?.cameras.length ?? 0) > 1"
            class="icon-btn"
            :aria-label="$t('aria.switchCamera')"
            @click="camera?.switchNext()"
          >
            <svg
              viewBox="0 0 24 24"
              width="21"
              height="21"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M17 2l4 4-4 4" />
              <path d="M3 12V10a4 4 0 0 1 4-4h14" />
              <path d="M7 22l-4-4 4-4" />
              <path d="M21 12v2a4 4 0 0 1-4 4H3" />
            </svg>
          </button>
          <button class="icon-btn" :aria-label="$t('aria.history')" @click="openHistory('all')">
            ≡
          </button>
        </div>
      </div>

      <div class="bottom-bar" v-if="phase === 'scanning'">
        <ZoomSlider
          v-if="camState"
          :zoom="camState.zoom"
          @change="(v: number) => camera?.setZoom(v)"
        />
      </div>

      <div v-if="suggestGuide && mode === 'auto'" class="toast" @click="switchToGuide">
        {{ $t('scan.slowToast') }}
      </div>

      <div v-if="galleryError" class="toast toast-static">{{ $t('manual.noPlateFound') }}</div>

      <button
        v-if="phase === 'scanning'"
        class="fab-gallery"
        :aria-label="$t('aria.gallery')"
        :disabled="galleryScanning"
        @click="pickGallery"
      >
        <span v-if="galleryScanning" class="spinner small-spinner" aria-hidden="true"></span>
        <svg
          v-else
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.9"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="4.5" width="18" height="15" rx="2" />
          <circle cx="8.5" cy="9.5" r="1.6" />
          <path d="M4 17l4.5-4.5 3.5 3.5 3-3L20 16.5" />
        </svg>
      </button>
      <input
        ref="galleryInput"
        class="visually-hidden"
        type="file"
        accept="image/*"
        @change="onGalleryFile"
      />
    </template>

    <ResultCard
      v-if="phase === 'locked' && locked"
      :result="locked"
      :lookup-status="lookupStatus"
      :lookup-error="lookupError"
      @copy="copyText"
      @favorite="scanner.toggleFavorite()"
      @edit="(t: string) => scanner.applyEdit(t)"
      @lookup="lookupPlate"
      @rescan="rescan"
    />

    <HistoryPanel
      v-if="showHistory"
      :initial-filter="historyFilter"
      @close="showHistory = false; void refreshHistoryPresence();"
      @open="(p: { plate: string; region?: string }) => { showHistory = false; lookupPlate(p.plate, p.region); }"
    />
    <DebugOverlay v-if="DEBUG" :debug="debug" :mode="mode" :vote="vote" />
  </div>
</template>
